import { Stage, Rect, ImageUrlSource, Tile } from 'marzipano';

import { CubemapFaces } from './marzipano-cubemap-loader.model';

// Type definitions for better type safety
interface ImageLoadCallback {
  (error: Error | null, asset?: MarzipanoAsset): void;
}

interface MarzipanoAsset {
  element: () => HTMLImageElement | HTMLCanvasElement | ImageBitmap;
  width: () => number;
  height: () => number;
  isDynamic: () => boolean;
  timestamp: () => number;
  destroy: () => void;
}

interface MarzipanoLoadCallback {
  (error: Error | null, asset?: MarzipanoAsset): void;
}

interface AuthenticatedImageLoader {
  // Must return a cancel function to satisfy Marzipano WorkQueue
  loadImage: (url: string, rect?: Rect, done?: MarzipanoLoadCallback) => () => void;
}

interface IImageLoadOptions {
  img: HTMLImageElement; // The loaded image element.
  x: number; // Crop x coordinate (normalized 0-1).
  y: number; // Crop y coordinate (normalized 0-1).
  width: number; // Crop width (normalized 0-1).
  height: number; // Crop height (normalized 0-1).
  done: ImageLoadCallback; // Completion callback.
}

/**
 * Custom Marzipano Image Loader that extends Marzipano's internal HtmlImageLoader functionality
 * with authentication support.
 *
 * This loader:
 * - Adds authentication headers to image requests
 * - Maintains compatibility with Marzipano's loading pipeline.
 *
 * Based on Marzipano's original HtmlImageLoader implementation.
 */
export class MarzipanoImageLoader {
  public loader: AuthenticatedImageLoader;

  /**
   *
   * @param onTileLoaded Callback to run after each tile load.
   */
  constructor(onTileLoaded?: () => void) {
    this.loader = this.createAuthenticatedLoader(onTileLoaded);
  }

  /**
   * Patch Marzipano's stage to use our authenticated image loader.
   *
   * @param stage - Marzipano stage to patch.
   */
  public patchStageForAuthentication(stage: Stage): void {
    stage.loadImage = this.loader.loadImage;
  }

  /**
   * Create ImageUrlSource for individual face URLs.
   * This creates a custom source that maps each face to its individual URL.
   *
   * @param faces - Cubemap face URLs.
   * @returns ImageUrlSource configured for individual face loading.
   */
  public createImageSource(faces: CubemapFaces): ImageUrlSource {
    // Create face mapping following Marzipano's cube face convention
    // Standard cubemap orientation: right, left, up, down, front, back
    const faceMapping: Record<string, string> = {
      'r': faces.px, // Right = positive X
      'l': faces.nx, // Left = negative X
      'u': faces.py, // Up = positive Y
      'd': faces.ny, // Down = negative Y
      'f': faces.pz, // Front = positive Z
      'b': faces.nz  // Back = negative Z
    };

    // Create source function that maps tiles to individual face URLs
    const sourceFromTile = (tile: Tile): { url: string } => {
      const face = tile.face || 'f'; // Default to front face
      const url = faceMapping[face] || faces.px;

      return { url };
    };

    // Create ImageUrlSource with custom tile mapping and optimized loading parameters.
    return new ImageUrlSource(sourceFromTile, {
      retryDelay: 500,
      concurrency: 6
    });
  }

  /**
   * Create a credentials-aware image loading function.
   * This replaces Marzipano's default image loading with authenticated requests.
   * @param onTileLoaded
   * @returns Authenticated image loader compatible with Marzipano's interface.
   */
  private createAuthenticatedLoader(onTileLoaded?: () => void): AuthenticatedImageLoader {
    return {
      loadImage: (url: string, rect?: Rect, done?: MarzipanoLoadCallback): () => void => {
        const img = new Image();

        img.crossOrigin = 'use-credentials';

        const x = rect?.x || 0;
        const y = rect?.y || 0;
        const width = rect?.width || 1;
        const height = rect?.height || 1;

        // Ensure done is only called once
        let completed = false;
        let cancelled = false;

        const onceDone: ImageLoadCallback = (error: Error | null, asset?: MarzipanoAsset): void => {
          if (!completed) {
            completed = true;
            if (done) {
              done(error, asset);
            }

            // Only count success tiles
            if (!error && onTileLoaded) {
              onTileLoaded();
            }
          }
        };

        img.onload = (): void => {
          if (!cancelled) {
            this.handleLoad({ img, x, y, width, height, done: onceDone });
          }
        };

        img.onerror = (): void => {
          if (!cancelled) {
            this.handleError(url, onceDone);
          }
        };

        // Start loading
        img.src = url;

        // Return canceller expected by Marzipano WorkQueue
        return (): void => {
          cancelled = true;
          img.onload = null;
          img.onerror = null;
        };
      }
    };
  }

  /**
   * Handle successful image loading with cropping support.
   *
   * @param options
   */
  private handleLoad(options: IImageLoadOptions): void {
    const { img, x, y, width, height, done } = options;

    if (x === 0 && y === 0 && width === 1 && height === 1) {
      // Fast path for when cropping is not needed
      done(null, this.createStaticAsset(img));
      return;
    }

    // Convert normalized coordinates to pixel coordinates
    const config: IImageLoadOptions = {
      img,
      x: x * img.naturalWidth,
      y: y * img.naturalHeight,
      width: width * img.naturalWidth,
      height: height * img.naturalHeight,
      done: done
    };

    // Use createImageBitmap if available for better performance
    if (this.supportsCreateImageBitmap()) {
      this.cropWithImageBitmap(config);
    } else {
      this.cropWithCanvas(config);
    }
  }

  /**
   * Handle image loading errors.
   * @param url - The URL that failed to load.
   * @param done - Completion callback.
   */
  private handleError(url: string, done: ImageLoadCallback): void {
    const error = new Error(`Network error: ${url}`);

    (error as Error & { name: string }).name = 'NetworkError';
    done(error);
  }

  /**
   * Create a static asset from an image or canvas.
   * @param element - The image, canvas, or ImageBitmap element.
   * @returns Asset-like object for Marzipano.
   */
  private createStaticAsset(element: HTMLImageElement | HTMLCanvasElement | ImageBitmap): MarzipanoAsset {
    return {
      element: (): HTMLImageElement | HTMLCanvasElement | ImageBitmap => element,
      width: (): number => {
        if (element instanceof ImageBitmap) {
          return element.width;
        }
        return element.width || (element as HTMLImageElement).naturalWidth;
      },
      height: (): number => {
        if (element instanceof ImageBitmap) {
          return element.height;
        }
        return element.height || (element as HTMLImageElement).naturalHeight;
      },
      isDynamic: (): boolean => false,
      timestamp: (): number => Date.now(),
      destroy: (): void => {
        if (element instanceof ImageBitmap) {
          element.close?.();
        }
      }
    };
  }

  /**
   * Check if createImageBitmap is supported and safe to use.
   * @returns True if createImageBitmap should be used.
   */
  private supportsCreateImageBitmap(): boolean {
    const hasCreateImageBitmap = !!(window as unknown as { createImageBitmap?: unknown }).createImageBitmap;
    const userAgent = navigator.userAgent.toLowerCase();
    const isFirefox = userAgent.includes('firefox');
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');

    return hasCreateImageBitmap && !isFirefox && !isSafari;
  }

  /**
   * Crop image using createImageBitmap for better performance.
   *
   * @param config
   */
  private cropWithImageBitmap(config: IImageLoadOptions): void {
    const options: ImageBitmapOptions = { imageOrientation: 'flipY', premultiplyAlpha: 'premultiply' };

    (window as unknown as { createImageBitmap: typeof createImageBitmap }).createImageBitmap(
      config.img, config.x, config.y, config.width, config.height, options
    )
      .then((bitmap: ImageBitmap) => {
        config.done(null, this.createStaticAsset(bitmap));
      })
      .catch(() => {
        // Fallback to canvas cropping
        this.cropWithCanvas(config);
      });
  }

  /**
   * Crop image using canvas as fallback.
   *
   * @param config
   */
  private cropWithCanvas(config: IImageLoadOptions): void {
    const { img, x, y, width, height, done } = config;

    const canvas = document.createElement('canvas');

    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');

    if (!context) {
      done(new Error('Failed to get 2D context'));
      return;
    }

    context.drawImage(img, x, y, width, height, 0, 0, width, height);
    done(null, this.createStaticAsset(canvas));
  }
}
